export type ErrorSource = 'CRM' | 'SMARTFLO';
export type ErrorCategory = 'VALIDATION' | 'AUTH' | 'NETWORK' | 'DATABASE' | 'EXTERNAL_SERVICE' | 'INTERNAL' | 'BUSINESS';

export interface ApiErrorDetail {
  errorId: string;
  code: string;
  source: ErrorSource;
  category: ErrorCategory;
  retryable: boolean;
  details?: { fields?: Record<string, string>; [key: string]: unknown };
  timestamp: string;
  stack?: string;
  raw?: string;
}

export interface ApiErrorResponse {
  success: false;
  message: string;
  error: ApiErrorDetail;
}

export class AppError extends Error {
  readonly errorId: string;
  readonly code: string;
  readonly source: ErrorSource;
  readonly category: ErrorCategory;
  readonly retryable: boolean;
  readonly details: { fields?: Record<string, string>; [key: string]: unknown } | null;
  readonly statusCode: number;
  readonly timestamp: string;
  readonly userMessage: string;

  constructor(response: ApiErrorResponse, statusCode?: number) {
    super(response.message);
    this.name = 'AppError';
    this.errorId = response.error.errorId;
    this.code = response.error.code;
    this.source = response.error.source;
    this.category = response.error.category;
    this.retryable = response.error.retryable;
    this.details = response.error.details || null;
    this.statusCode = statusCode || 500;
    this.timestamp = response.error.timestamp;
    this.userMessage = response.message;
  }

  static fromRaw(message: string, statusCode: number): AppError {
    return new AppError({
      success: false,
      message,
      error: {
        errorId: 'ERR-unknown',
        code: 'INTERNAL_ERROR',
        source: 'CRM',
        category: 'INTERNAL',
        retryable: false,
        timestamp: new Date().toISOString(),
      },
    }, statusCode);
  }

  static networkError(): AppError {
    return new AppError({
      success: false,
      message: 'Unable to connect to the server. Please check your internet connection.',
      error: {
        errorId: 'ERR-network',
        code: 'NETWORK_ERROR',
        source: 'CRM',
        category: 'NETWORK',
        retryable: true,
        timestamp: new Date().toISOString(),
      },
    }, 0);
  }

  static timeoutError(): AppError {
    return new AppError({
      success: false,
      message: 'The request took too long. Please try again.',
      error: {
        errorId: 'ERR-timeout',
        code: 'REQUEST_TIMEOUT',
        source: 'CRM',
        category: 'NETWORK',
        retryable: true,
        timestamp: new Date().toISOString(),
      },
    }, 408);
  }
}
